import { getPermalinkEncodedFilterKeys } from '@/features/seo-listing/consts';
import { getBrowserPathname } from '../get-browser-pathname.util';

export interface ListingQueryFilters {
  page?: number;
  sort?: string;
  search?: string;
  fandoms?: string[];
  tags?: string[];
  universes?: string[];
  statuses?: string[];
  ratings?: string[];
  characters?: string[];
  pairingCharacters?: string[];
}

/**
 * Builds the listing href (pathname + query) for the given filters.
 *
 * On a permalink route (e.g. /fanfics/fandoms/naruto) a filter dimension that is
 * already encoded in the path must not be duplicated into the query. Otherwise
 * changing a non-canonical filter (ratings/statuses, which does not trigger SEO
 * navigation) would append a stale path-encoded value back into the query, e.g.
 * /fanfics/fandoms/naruto?fandoms=10529&ratings=0 instead of
 * /fanfics/fandoms/naruto?ratings=0.
 */
export const buildListingQueryHref = (pathname: string, params: ListingQueryFilters): string => {
  const urlParams = new URLSearchParams();
  const permalinkEncodedKeys = getPermalinkEncodedFilterKeys(pathname);

  if (params.page && params.page > 1) urlParams.set('page', params.page.toString());
  if (params.sort && params.sort !== 'hotNew') urlParams.set('sort', params.sort);
  if (params.search) urlParams.set('search', params.search);
  if (params.fandoms && params.fandoms.length > 0 && !permalinkEncodedKeys.includes('fandoms'))
    urlParams.set('fandoms', params.fandoms.join(','));
  if (params.tags && params.tags.length > 0 && !permalinkEncodedKeys.includes('tags'))
    urlParams.set('tags', params.tags.join(','));
  if (params.universes && params.universes.length > 0)
    urlParams.set('universes', params.universes.join(','));
  if (params.statuses && params.statuses.length > 0)
    urlParams.set('statuses', params.statuses.join(','));
  if (params.ratings && params.ratings.length > 0)
    urlParams.set('ratings', params.ratings.join(','));
  if (
    params.characters &&
    params.characters.length > 0 &&
    !permalinkEncodedKeys.includes('characters')
  )
    urlParams.set('characters', params.characters.join(','));
  if (
    params.pairingCharacters &&
    params.pairingCharacters.length > 0 &&
    !permalinkEncodedKeys.includes('pairingCharacters')
  )
    urlParams.set('pairingCharacters', params.pairingCharacters.join(','));

  const queryString = urlParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

/**
 * Writes the listing filters into the address bar via pushState.
 * window.location.pathname is used (not Next.js usePathname) because the client
 * SEO redirect uses pushState and usePathname() stays on /fanfics.
 */
export const pushListingFiltersToUrl = (params: ListingQueryFilters): void => {
  const newUrl = buildListingQueryHref(getBrowserPathname(), params);
  const currentUrl = window.location.pathname + window.location.search;

  if (currentUrl !== newUrl) {
    // pushState keeps each filter step (status, rating, pass-through tag)
    // on the stack. replaceState overwrote the previous entry, so Back
    // jumped straight to /fanfics and skipped those selections.
    window.history.pushState({}, '', newUrl);
  }
};
